import { applyCspNonce } from "./csp";

const SITE_NAME = "AetherCV";
const DEFAULT_TITLE = `${SITE_NAME} | AI Resume Builder, Job Applications & Interview Prep`;
const DEFAULT_DESCRIPTION =
  "Build ATS-ready, role-based resumes with AetherCV AI Resume Builder. Improve job applications and interview preparation with one platform.";
const DEFAULT_KEYWORDS =
  "AetherCV, Aether CV, AI resume builder, resume generator, AI resume, ATS resume checker, job applications, interview preparation, role-based resume, CV builder";
const DEFAULT_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const DEFAULT_OG_TYPE = "website";
const DEFAULT_OG_LOCALE = "en_US";
const DEFAULT_TWITTER_CARD = "summary_large_image";
const DEFAULT_OG_IMAGE_ALT = "AetherCV preview";

const normalizeUrl = (value) => String(value || "").trim().replace(/\/+$/, "");
const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value);

const toAbsoluteUrl = (value, siteUrl) => {
  if (!value) return "";
  if (isAbsoluteUrl(value)) return value;
  const base = normalizeUrl(siteUrl);
  if (!base) return value;
  if (value.startsWith("/")) return `${base}${value}`;
  return `${base}/${value}`;
};

const resolveCanonicalUrl = (canonicalPath, siteUrl) => {
  if (!canonicalPath) return normalizeUrl(siteUrl);
  if (isAbsoluteUrl(canonicalPath)) return canonicalPath;
  const base = normalizeUrl(siteUrl);
  if (!base) return canonicalPath;
  const path = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
  return `${base}${path}`;
};

export const getSiteUrl = () => {
  const envUrl = typeof import.meta !== "undefined" ? import.meta.env?.VITE_SITE_URL : "";
  if (envUrl) return normalizeUrl(envUrl);
  if (typeof window !== "undefined" && window.location?.origin) {
    return normalizeUrl(window.location.origin);
  }
  return "";
};

export const getOgImageUrl = (siteUrl) => {
  const envImage = typeof import.meta !== "undefined" ? import.meta.env?.VITE_OG_IMAGE : "";
  const image = envImage || "/logo.png";
  return toAbsoluteUrl(image, siteUrl);
};

const ensureMetaTag = (selector, attrs) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attrs).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }
  return tag;
};

const setMetaContent = ({ name, property, content }) => {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  const attrs = name ? { name } : { property };
  const tag = ensureMetaTag(selector, attrs);
  tag.setAttribute("content", content);
};

const setLink = ({ rel, href }) => {
  if (!href) return;
  const selector = `link[rel="${rel}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

const normalizeStructuredData = (structuredData) => {
  if (!structuredData) return [];
  const entries = Array.isArray(structuredData) ? structuredData : [structuredData];
  return entries
    .map((entry, index) => {
      if (!entry) return null;
      const id = entry.id || `structured-data-${index}`;
      const data = entry.data || entry;
      return { id, data };
    })
    .filter(Boolean);
};

const removeUnmanagedStructuredData = () => {
  const unmanaged = Array.from(
    document.head.querySelectorAll('script[type="application/ld+json"]:not([data-seo])')
  );
  unmanaged.forEach((script) => script.remove());
};

const syncStructuredData = (entries) => {
  removeUnmanagedStructuredData();

  const managed = Array.from(
    document.head.querySelectorAll('script[type="application/ld+json"][data-seo]')
  );
  const keep = new Set(entries.map((entry) => entry.id));
  managed.forEach((script) => {
    const id = script.getAttribute("data-seo");
    if (!keep.has(id)) script.remove();
  });

  entries.forEach((entry) => {
    const selector = `script[type="application/ld+json"][data-seo="${entry.id}"]`;
    let script = document.head.querySelector(selector);
    if (!script) {
      script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.setAttribute("data-seo", entry.id);
      applyCspNonce(script);
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(entry.data);
  });
};

export const updateSeo = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonicalPath = "/",
  canonicalUrl,
  robots = DEFAULT_ROBOTS,
  googlebot,
  bingbot,
  ogImage,
  ogType = DEFAULT_OG_TYPE,
  ogLocale = DEFAULT_OG_LOCALE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  twitterCard = DEFAULT_TWITTER_CARD,
  structuredData,
} = {}) => {
  if (typeof document === "undefined") return;

  const siteUrl = getSiteUrl();
  const resolvedCanonicalUrl = canonicalUrl || resolveCanonicalUrl(canonicalPath, siteUrl);
  const resolvedOgImage = toAbsoluteUrl(ogImage || getOgImageUrl(siteUrl), siteUrl);

  if (title) document.title = title;

  setMetaContent({ name: "description", content: description });
  setMetaContent({ name: "keywords", content: keywords });
  setMetaContent({ name: "robots", content: robots });
  setMetaContent({ name: "googlebot", content: googlebot || robots });
  setMetaContent({ name: "bingbot", content: bingbot || robots });

  setMetaContent({ property: "og:type", content: ogType });
  setMetaContent({ property: "og:site_name", content: SITE_NAME });
  setMetaContent({ property: "og:title", content: title });
  setMetaContent({ property: "og:description", content: description });
  setMetaContent({ property: "og:url", content: resolvedCanonicalUrl });
  setMetaContent({ property: "og:image", content: resolvedOgImage });
  setMetaContent({ property: "og:image:alt", content: ogImageAlt });
  setMetaContent({ property: "og:locale", content: ogLocale });

  setMetaContent({ name: "twitter:card", content: twitterCard });
  setMetaContent({ name: "twitter:title", content: title });
  setMetaContent({ name: "twitter:description", content: description });
  setMetaContent({ name: "twitter:image", content: resolvedOgImage });
  setMetaContent({ name: "twitter:image:alt", content: ogImageAlt });
  setMetaContent({ name: "twitter:url", content: resolvedCanonicalUrl });

  setLink({ rel: "canonical", href: resolvedCanonicalUrl });

  const normalizedStructuredData = normalizeStructuredData(structuredData);
  syncStructuredData(normalizedStructuredData);
};
